import ReactGA from 'react-ga';
import { ENVIRONMENT } from '../constants';

/**
 * @typedef GAConfig
 * @property {string} category
 * @property {string} action
 * @property {string} label
 * @property {number} value
 * @property {boolean} isNotAnInteraction
 */

interface GAConfig {
  /** A top level category for these events. E.g. 'User', 'Navigation', 'App Editing', etc. */
  category: string;
  /** A description of the behaviour. E.g. 'Clicked Delete', 'Added a component', 'Deleted account', etc. */
  action: string;
  /** More precise labelling of the related action. E.g. alongside the 'Added a component' action, we could add the name of a component as the label. E.g. 'Survey', 'Heading', 'Button', etc. */
  label: string;
  /** A means of recording a numerical value against an event. E.g. a rating, a score, etc. */
  value?: number;
  /** Optional. If an event is not triggered by a user interaction, but instead by our code (e.g. on page load, it should be flagged as a nonInteraction event to avoid skewing bounce rate data. */
  nonInteraction?: boolean;
}

export const trackEvent = (config: GAConfig) => {
  if (ENVIRONMENT !== 'production' || typeof document === 'undefined') {
    return console.log('DEBUG_GA: ', config);
  }

  ReactGA.event({
    category: config.category,
    label: config.label,
    action: config.action,
    value: config.value ? config.value : undefined,
    nonInteraction: config.nonInteraction ? config.nonInteraction : undefined,
  });
};

/**
 * @example
 *  const arr = <const>['testAction'];
 *  const tracker = createTrack<typeof arr[number]>('TEST', arr);
 */
export const createTracker = <T extends string, K extends string>(
  category: GAConfig['category'],
  actions: string[],
  labels: string[],
) => {
  const makeLabels = (
    actionName: string,
  ): {
    [Key in K]: (value?: GAConfig['value'], nonInteraction?: GAConfig['nonInteraction']) => void;
  } => {
    return labels.reduce(
      (obj, labelName) => ({
        ...obj,
        [labelName]: (value: GAConfig['value'], nonInteraction: GAConfig['nonInteraction']) =>
          trackEvent({ category, label: labelName, action: actionName, value, nonInteraction }),
      }),
      {} as any,
    );
  };

  const result: {
    [Key in T]: () => ReturnType<typeof makeLabels>;
  } = actions.reduce(
    (obj, actionName) => ({
      ...obj,
      [actionName]: () => makeLabels(actionName),
    }),
    {} as any,
  );
  return result;
};

const EVENTS = <const>['click', 'hover', 'submit', 'change'];

const MANAGE_ANIMALS_TRACKED_EVENTS = <const>[
  'CONFIRMED_DELETE_ANIMAL',
  'TOGGLE_ACTIVE',
  'FILTER_ACTIVE',
  'FILTER_ANUMBER',
  'FILTER_NAME',
  'FILTER_ANIMAL_TYPE',
  'FILTER_LOCATION',
];
const DOG_LIST_TRACKED_EVENTS = <const>['BACK_TO_TOP', 'OPEN_ANIMAL_COMMENTS'];
const CAT_LIST_TRACKED_EVENTS = <const>['BACK_TO_TOP', 'OPEN_ANIMAL_COMMENTS', 'VIEW_PATIO_RULES'];

export const trackers = {
  manageAnimals: createTracker<typeof EVENTS[number], typeof MANAGE_ANIMALS_TRACKED_EVENTS[number]>(
    'Manage Animals',
    (EVENTS as unknown) as string[],
    (MANAGE_ANIMALS_TRACKED_EVENTS as unknown) as string[],
  ),
  dogList: createTracker<typeof EVENTS[number], typeof DOG_LIST_TRACKED_EVENTS[number]>(
    'Dog Enrichment List',
    (EVENTS as unknown) as string[],
    (DOG_LIST_TRACKED_EVENTS as unknown) as string[],
  ),
  catList: createTracker<typeof EVENTS[number], typeof CAT_LIST_TRACKED_EVENTS[number]>(
    'Cat Enrichment List',
    (EVENTS as unknown) as string[],
    (CAT_LIST_TRACKED_EVENTS as unknown) as string[],
  ),
};
